import React from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Alert, Stack } from "@mui/material";

import { useCanSubstituteAttendee } from "../../store";
import ClearCallLoadingLogo from "../../components/clear-call-loading-logo";
import { ClearCallLogoTitle } from "../../components/clear-call-logo";
import MeetingSubstituteInvitePanel from "./components/meeting-substitute-invite-panel";

const InviteErrorAlert: React.FC<{ message: string }> = ({ message }) => (
  <Alert
    severity="error"
    sx={{
      fontSize: "1.25rem",
      justifyContent: "center",
      border: "1px solid",
      borderColor: "error.main",
      backgroundColor: "rgba(255,255,255,0.7)",
      "& .MuiAlert-icon": { fontSize: "2rem" },
      "& .MuiAlert-message": { textAlign: "center" },
    }}
  >
    {message}
  </Alert>
);

const MeetingInvitePage: React.FC = () => {
  const { meetingId } = useParams<{ meetingId: string }>();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get("userId") || "";
  const joinCode = searchParams.get("joinCode") || "";

  const {
    data: canSubstituteData,
    isLoading,
    isError
  } = useCanSubstituteAttendee(meetingId || "", userId, joinCode);

  if (isLoading) {
    return (
      <Stack
        justifyContent="center"
        alignItems="center"
        sx={{
          minHeight: "100vh",
          background:
            "linear-gradient(0deg,rgb(222, 238, 253),rgb(172, 220, 226))",
        }}
      >
        <ClearCallLoadingLogo />
      </Stack>
    );
  }

  let content: React.ReactNode;

  if (!joinCode || !userId || !meetingId) {
    content = <InviteErrorAlert message="Invalid meeting confirmation link" />;
  } else if (isError || !canSubstituteData) {
    content = <InviteErrorAlert message="An error occurred while loading the meeting." />;
  } else if (canSubstituteData.meetingType === "Abandoned") {
    content = <InviteErrorAlert message="This meeting has been abandoned." />;
  } else if (canSubstituteData.meetingType === "Cancelled") {
    content = <InviteErrorAlert message="This meeting has been cancelled." />;
  } else if (canSubstituteData.meetingType === "Ended") {
    content = <InviteErrorAlert message="This meeting has been completed." />;
  } else {
    content = (
      <MeetingSubstituteInvitePanel
        joinCode={joinCode}
        userId={userId}
        meetingId={meetingId}
        canSubstituteData={canSubstituteData}
      />
    );
  }

  return (
    <Stack
      justifyContent="center"
      alignItems="center"
      spacing={5}
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(0deg,rgb(222, 238, 253),rgb(172, 220, 226))",
      }}
    >
      <ClearCallLogoTitle size={200} />

      {content}
    </Stack>
  );
};

export default MeetingInvitePage;
